/**
 * Bulk Upload Validation Utilities
 * Provides validation and sanitization functions for Information Manager bulk upload
 */

import {
  DUE_DAYS_MAX,
  DUE_DAYS_MIN,
  ERROR_MESSAGES,
  INFORMATION_MANAGER_TYPE_OPTIONS,
} from './constants';

const EMAIL_PATTERN = /^([a-zA-Z0-9_\-.+]+)@([a-zA-Z0-9\-.]+)\.([a-zA-Z]{2,})$/;

// Valid priority values (case-insensitive) → canonical display casing
const PRIORITY_ALIASES = {
  high: 'High',
  h: 'High',
  medium: 'Medium',
  med: 'Medium',
  m: 'Medium',
  low: 'Low',
  l: 'Low',
};

/**
 * Map display labels / legacy aliases to canonical request type codes.
 * Canonical: text | fileupload | link
 */
const TYPE_ALIASES = {
  text: 'text',
  written: 'text',
  'written response': 'text',
  'writtenresponse': 'text',
  number: 'number',
  fileupload: 'fileupload',
  'file upload': 'fileupload',
  file: 'fileupload',
  upload: 'fileupload',
  link: 'link',
  url: 'link',
  'url link': 'link',
  urllink: 'link',
  hyperlink: 'link',
  fileuploadtolink: 'link',
  'file upload link': 'link',
  'file upload to link': 'link',
  'fileupload to link': 'link',
  multiselect: 'multiselect',
  'multi select': 'multiselect',
  choice: 'choice',
};

const VALID_CANONICAL_TYPES = new Set([
  'text',
  'number',
  'fileupload',
  'link',
  'multiselect',
  'choice',
]);

const PRIORITY_ERROR_MESSAGE = 'Invalid priority. Must be one of: High, Medium, Low (or H/M/L, Med)';
const TYPE_ERROR_MESSAGE = `Invalid type. Must be one of: ${INFORMATION_MANAGER_TYPE_OPTIONS.map((o) => o.name).join(', ')} (or text / fileupload / link)`;

/**
 * Sanitize and validate priority field
 * @param {string} priority - Raw priority value from upload
 * @returns {Object} { value: sanitizedValue, error: errorMessage }
 */
export function sanitizePriority(priority) {
  if (priority === null || priority === undefined || priority.toString().trim() === '') {
    // Empty priority defaults to Medium (cycle bulk behaviour); masters may treat as error via caller
    return { value: 'Medium', error: null };
  }

  const sanitized = priority.toString().trim().toLowerCase();
  const mapped = PRIORITY_ALIASES[sanitized];

  if (mapped) {
    return { value: mapped, error: null };
  }

  return {
    value: priority.toString().trim(),
    error: PRIORITY_ERROR_MESSAGE,
  };
}

/**
 * Normalize a raw type string to a canonical code when possible.
 * @param {string} type
 * @returns {string|null}
 */
export function normalizeTypeAlias(type) {
  if (type === null || type === undefined || type.toString().trim() === '') {
    return null;
  }

  const raw = type.toString().trim();
  const lower = raw.toLowerCase();
  const compact = lower.replace(/[\s_-]+/g, '');

  if (TYPE_ALIASES[lower]) return TYPE_ALIASES[lower];
  if (TYPE_ALIASES[compact]) return TYPE_ALIASES[compact];

  // Match INFORMATION_MANAGER_TYPE_OPTIONS labels case-insensitively
  const optionMatch = INFORMATION_MANAGER_TYPE_OPTIONS.find(
    (opt) => opt.name.toLowerCase() === lower || opt.value.toLowerCase() === lower,
  );
  if (optionMatch) return optionMatch.value === 'fileuploadtolink' ? 'link' : optionMatch.value;

  if (VALID_CANONICAL_TYPES.has(lower)) return lower;

  return null;
}

/**
 * Sanitize and validate type field
 * @param {string} type - Raw type value from upload
 * @param {Object} [options]
 * @param {boolean} [options.required=true] - When false, empty type is allowed (returns empty value)
 * @param {string[]} [options.allowed] - Restrict to a subset of canonical types
 * @returns {Object} { value: sanitizedValue, error: errorMessage }
 */
export function sanitizeType(type, options = {}) {
  const { required = true, allowed = null } = options;

  if (type === null || type === undefined || type.toString().trim() === '') {
    if (!required) {
      return { value: '', error: null };
    }
    return { value: '', error: TYPE_ERROR_MESSAGE };
  }

  const normalized = normalizeTypeAlias(type);

  if (!normalized) {
    return {
      value: type.toString().trim(),
      error: TYPE_ERROR_MESSAGE,
    };
  }

  if (Array.isArray(allowed) && allowed.length > 0 && !allowed.includes(normalized)) {
    return {
      value: normalized,
      error: TYPE_ERROR_MESSAGE,
    };
  }

  return { value: normalized, error: null };
}

/**
 * Human-readable label for a canonical request type.
 * @param {string} type
 * @returns {string}
 */
export function formatRequestTypeLabel(type) {
  const normalized = normalizeTypeAlias(type) || (type || '').toString().trim();
  const match = INFORMATION_MANAGER_TYPE_OPTIONS.find((opt) => opt.value === normalized);
  if (match) return match.name;
  if (normalized === 'fileuploadtolink') return 'Link';
  return normalized || '';
}

/**
 * Trim string input safely
 * @param {any} value - Input value to trim
 * @returns {string} Trimmed string value
 */
export function trimInput(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return value.toString().trim();
}

/**
 * Parse and sanitize checklist references
 * @param {string} checklistRef - Comma-separated checklist references
 * @returns {Array} Array of trimmed references
 */
export function sanitizeChecklistRef(checklistRef) {
  if (!checklistRef) return [];
  return checklistRef
    .toString()
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x !== '');
}

/**
 * Validate signed due-days offset as a whole number in the inclusive range [-200, 200].
 * Empty values are allowed unless required is true.
 * @param {any} value
 * @param {{ required?: boolean }} [options]
 * @returns {{ value: string|number, error: string|null }}
 */
export function validateDueDays(value, options = {}) {
  const { required = false } = options;
  const raw = trimInput(value);

  if (raw === '') {
    if (required) {
      return { value: '', error: ERROR_MESSAGES.DUE_DAYS_REQUIRED };
    }
    return { value: '', error: null };
  }

  const num = Number(raw);
  if (!Number.isFinite(num)) {
    return { value: raw, error: ERROR_MESSAGES.DUE_DAYS_NUMBER };
  }
  if (!Number.isInteger(num)) {
    return { value: num, error: ERROR_MESSAGES.DUE_DAYS_INTEGER };
  }
  if (num < DUE_DAYS_MIN || num > DUE_DAYS_MAX) {
    return { value: num, error: ERROR_MESSAGES.DUE_DAYS_RANGE };
  }
  return { value: num, error: null };
}

const UPLOAD_LINK_SCHEMES = new Set(['http', 'https', 'ftp', 'ftps', 'sftp', 'file']);

/** Hosts used by OneDrive, SharePoint, and Google Drive share links. */
const STORAGE_HOST_SUFFIXES = [
  'sharepoint.com',
  'sharepoint.de',
  'sharepoint.us',
  'sharepoint.cn',
  'sharepoint-df.com',
  '1drv.ms',
  'onedrive.live.com',
  'onedrive.com',
  'drive.google.com',
  'docs.google.com',
  'drive.usercontent.google.com',
];

/**
 * UNC paths (\\server\share\...) used as file locations.
 * @param {string} value
 * @returns {boolean}
 */
function isUncPath(value) {
  return /^\\\\[^\\]+\\[^\\]+/.test(value);
}

/**
 * Copied SharePoint, OneDrive, and Drive links often contain spaces or
 * characters the URL parser rejects. Encode those without changing the link.
 * @param {string} value
 * @returns {string}
 */
function relaxCopiedLink(value) {
  return value
    .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/ /g, '%20')
    .replace(/[{}|\\^`]/g, (char) => encodeURIComponent(char));
}

/**
 * @param {string} hostname
 * @returns {boolean}
 */
function isStorageHost(hostname) {
  const host = (hostname || '').toLowerCase();
  return STORAGE_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
}

/**
 * A leading token is a URI scheme only when it has no dot.
 * That keeps host:port values such as example.com:8080/path as addresses.
 * @param {string} value
 * @returns {{ href: string, suppliedScheme: boolean }|null}
 */
function toUploadLinkCandidate(value) {
  const unwrapped = value.replace(/^[<'"`]+|[>'"`]+$/g, '');
  const schemeMatch = unwrapped.match(/^([a-z][a-z0-9+-]*):/i);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (!UPLOAD_LINK_SCHEMES.has(scheme)) return null;
    return { href: relaxCopiedLink(unwrapped), suppliedScheme: true };
  }
  if (unwrapped.startsWith('//')) {
    return { href: `https:${relaxCopiedLink(unwrapped)}`, suppliedScheme: false };
  }
  return { href: `https://${relaxCopiedLink(unwrapped)}`, suppliedScheme: false };
}

/**
 * Accept http(s), ftp, ftps, sftp, file, UNC, OneDrive, SharePoint, Google Drive,
 * and schemeless addresses such as example.com/path.
 * A schemeless value still needs a hostname (a dot, localhost, an IPv4 address, or a storage host).
 * Returns the href to persist (scheme added when the user omitted one), or null.
 * @param {string} value
 * @returns {string|null}
 */
function normalizeUploadLink(value) {
  if (isUncPath(value)) return value;
  const candidate = toUploadLinkCandidate(value);
  if (!candidate) return null;
  try {
    const url = new URL(candidate.href);
    const scheme = url.protocol.replace(':', '').toLowerCase();
    if (!UPLOAD_LINK_SCHEMES.has(scheme)) return null;
    if (scheme === 'file') {
      return url.pathname && url.pathname !== '/' ? candidate.href : null;
    }
    const host = url.hostname;
    if (!host) return null;
    if (candidate.suppliedScheme || isStorageHost(host)) return candidate.href;
    const hasHost = host === 'localhost' || host.includes('.') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
    return hasHost ? candidate.href : null;
  } catch (err) {
    return null;
  }
}

/**
 * Validate a file-upload link without requiring a single URL shape.
 * `value` is the href to persist. Schemeless input such as example.com/path
 * is returned with https:// so anchors do not resolve against the app route.
 * @param {any} value
 * @param {{ required?: boolean }} [options]
 * @returns {{ value: string, error: string|null }}
 */
export function validateUploadLink(value, options = {}) {
  const { required = false } = options;
  const raw = trimInput(value);
  if (!raw) {
    return {
      value: '',
      error: required ? ERROR_MESSAGES.UPLOAD_LINK_REQUIRED : null,
    };
  }
  const normalized = normalizeUploadLink(raw);
  if (!normalized) {
    return { value: raw, error: ERROR_MESSAGES.UPLOAD_LINK_INVALID };
  }
  return { value: normalized, error: null };
}

/**
 * Case-insensitive match against active and inactive info owners.
 * @param {Array<{ email?: string, name?: string }>} owners
 * @param {any} email
 * @returns {Object|null}
 */
export function findInfoOwnerByEmail(owners, email) {
  const key = trimInput(email).toLowerCase();
  if (!key) return null;
  return (owners || []).find((owner) => trimInput(owner && owner.email).toLowerCase() === key) || null;
}

/**
 * Existing owners are matched by email. Their stored name is kept.
 * The uploaded name is used only when the email is not already an owner.
 * @param {any} email
 * @param {any} uploadedName
 * @param {Array} [owners]
 * @returns {{ name: string, email: string, matched: boolean }}
 */
export function resolveBulkInfoOwner(email, uploadedName, owners = []) {
  const uploaded = trimInput(uploadedName);
  const normalizedEmail = trimInput(email);
  const existing = findInfoOwnerByEmail(owners, normalizedEmail);
  if (!existing) {
    return { name: uploaded, email: normalizedEmail, matched: false };
  }
  return {
    name: trimInput(existing.name) || uploaded,
    email: trimInput(existing.email) || normalizedEmail,
    matched: true,
  };
}

/**
 * @param {any} email
 * @param {{ required?: boolean }} [options]
 * @returns {{ value: string, error: string|null }}
 */
export function validateEmail(email, options = {}) {
  const { required = true } = options;
  const value = trimInput(email);

  if (value === '') {
    if (required) {
      return { value: '', error: ERROR_MESSAGES.EMAIL_ID_REQUIRED };
    }
    return { value: '', error: null };
  }

  if (!EMAIL_PATTERN.test(value)) {
    return { value, error: ERROR_MESSAGES.INFO_OWNER_EMAIL_INVALID };
  }
  return { value, error: null };
}

/**
 * Flatten API field errors ({ field: ['msg'] } or nested) into { field: 'msg' }.
 * @param {Object} fieldErrors
 * @returns {Object}
 */
export function flattenApiFieldErrors(fieldErrors) {
  const out = {};
  if (!fieldErrors || typeof fieldErrors !== 'object' || Array.isArray(fieldErrors)) {
    return out;
  }
  Object.keys(fieldErrors).forEach((key) => {
    const val = fieldErrors[key];
    if (Array.isArray(val)) {
      const joined = val.filter(Boolean).join(', ');
      if (joined) out[key] = joined;
    } else if (val && typeof val === 'object') {
      const joined = Object.values(val).flat().filter(Boolean).join(', ');
      if (joined) out[key] = joined;
    } else if (val) {
      out[key] = String(val);
    }
  });
  return out;
}

/**
 * First non-empty message from a field-error map.
 * @param {Object} errors
 * @returns {string}
 */
export function firstErrorMessage(errors) {
  return Object.values(errors || {}).find((msg) => typeof msg === 'string' && msg.trim()) || '';
}
